/**
 * PATH: packages/kanban-host/src/allocation/AllocationWhyPopover.helpers.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-WHY-HELPERS-001
 * PURPOSE: Pure I→O helpers used by `AllocationWhyPopover.tsx`.
 *          Split out to keep the component file under the 300-line
 *          guideline AND so the helpers are directly unit-testable
 *          (no React render needed).
 *
 * WHY: The popover's sections (placement reason, History,
 *      Lifecycle) all need defensive transforms of the BE's
 *      free-form `classification_snapshot` and `audit_log_excerpt`
 *      payloads. Wrapping every guard inline in JSX would balloon
 *      the component file past 500 lines; pulling each into a
 *      named pure helper here keeps the component readable AND
 *      lets the tests pin every guard branch.
 *
 * DEPENDENCIES: none — by design.
 *
 * RELATED:
 *   - ./AllocationWhyPopover.tsx (consumer)
 *   - ./AllocationWhyPopover.helpers.test.ts (every branch pinned)
 */

export interface ConfidencePill {
  label: string
  tone: 'green' | 'amber' | 'muted'
}

/**
 * Format a 0.0–1.0 confidence pill. RT-03: only prefix "AI" when
 * decision_type is model (or unknown/legacy missing). Rule/gate/
 * manual must not look like model confidence.
 */
export function formatConfidencePill(
  confidence: number | null,
  decisionType?: string | null,
): ConfidencePill | null {
  if (confidence == null) return null
  const pct = Math.round(confidence * 100)
  const useAiPrefix =
    decisionType == null ||
    decisionType === '' ||
    decisionType === 'model' ||
    decisionType === 'legacy'
  const label = useAiPrefix ? `AI ${pct}%` : `${pct}%`
  if (confidence >= 0.8) {
    return { label, tone: 'green' }
  }
  if (confidence >= 0.6) {
    return { label, tone: 'amber' }
  }
  return { label, tone: 'muted' }
}

/** RT-03 section title — never "AI reasoning" for rule/gate/manual. */
export function decisionReasoningTitle(
  decisionType?: string | null,
): string {
  switch (decisionType) {
    case 'rule':
      return 'Rule match'
    case 'gate':
      return 'Deterministic gate'
    case 'manual':
      return 'Manual placement'
    case 'corrected':
      return 'Corrected placement'
    case 'failed':
      return 'Routing failure'
    case 'legacy':
      return 'Legacy placement'
    case 'model':
      return 'Model reasoning'
    default:
      return 'Placement reason'
  }
}

export interface TopCandidate {
  label: string
  score: number | null
}

/**
 * Pull the "top candidates" array off the AI's classification
 * snapshot. Tolerant of:
 *   - missing key (older snapshots)
 *   - non-array values (defensive against malformed JSON)
 *   - extra fields (free-form shape per plan §A.1.bis)
 *
 * Returns an empty array when no candidates were recorded so the
 * popover can simply hide the section instead of guarding inline.
 */
export function extractTopCandidates(
  snapshot: Record<string, unknown>,
): TopCandidate[] {
  const raw = snapshot?.['top_candidates']
  if (!Array.isArray(raw)) return []
  return raw
    .map((entry): TopCandidate | null => {
      if (!entry || typeof entry !== 'object') return null
      const obj = entry as Record<string, unknown>
      const label =
        (typeof obj['board_name'] === 'string' && obj['board_name']) ||
        (typeof obj['board'] === 'string' && obj['board']) ||
        (typeof obj['lane_name'] === 'string' && obj['lane_name']) ||
        (typeof obj['board_id'] === 'string' && obj['board_id']) ||
        null
      const scoreRaw = obj['score']
      const score = typeof scoreRaw === 'number' ? scoreRaw : null
      if (!label) return null
      return { label, score }
    })
    .filter((c): c is TopCandidate => c !== null)
}

/**
 * FEAT-2026-07-15-EXPLAIN-BOARD-CONTEXT (change #29) — human "where"
 * line for the popover header. Pure I → O:
 *
 *   ({board_name:'Error Log Live', lane_name:'System notifications',
 *     parent_board_name:'Inbox'})
 *     → 'Inbox › Error Log Live › System notifications'
 *
 * Missing pieces are skipped (older BE payloads omit all of them →
 * returns null so the section hides).
 */
export function buildWherePath(data: {
  board_name?: string
  lane_name?: string
  parent_board_name?: string | null
}): string | null {
  const parts = [
    data.parent_board_name || null,
    data.board_name || null,
    data.lane_name || null,
  ].filter((p): p is string => Boolean(p))
  return parts.length > 0 ? parts.join(' › ') : null
}

/**
 * RT-03 (#128): explanation prose must cite persisted decision evidence
 * only. Pure I → O:
 *
 *   - non-empty `evidence.reason` (from EmailDecisionJournal) → return it
 *   - otherwise null → the popover hides the section (never synthesize
 *     "Filed by rule…" / "Auto-classified…" / AI-summary-as-why copy)
 *
 * The second/third args are accepted for call-site compatibility but
 * ignored — INV-03 forbids inventing Why text from snapshots or summaries.
 */
export function buildExplanationText(
  _snapshot?: Record<string, unknown>,
  _emailAiSummary?: string,
  evidenceReason?: string | null,
): string | null {
  const reason = (evidenceReason || '').trim()
  return reason || null
}

/**
 * Render an audit-log event as a human line. Pure I → O so the
 * popover's chronological strip can be unit-tested without
 * mounting the component.
 */
export function describeAuditEvent(entry: Record<string, unknown>): string {
  const event = typeof entry['event'] === 'string' ? entry['event'] : 'event'
  switch (event) {
    case 'alloc_created':
      return 'Allocation created'
    case 'alloc_ai_confirmed':
      return 'AI suggestion accepted by operator'
    case 'alloc_ai_rejected':
      return 'AI suggestion rejected by operator'
    case 'alloc_revoked': {
      const reason = entry['reason']
      const superseded = entry['superseded_by_allocation_id']
      if (typeof superseded === 'string' && superseded) {
        return `Superseded by a new allocation (${
          typeof reason === 'string' ? reason : 'override'
        })`
      }
      return `Allocation revoked${
        typeof reason === 'string' && reason ? ` — ${reason}` : ''
      }`
    }
    case 'alloc_moved':
      return 'Allocation moved between lanes'
    default:
      return `Event: ${event}`
  }
}
